<template>

  <div>
    <div ref="chartContainer" style=" min-height: 400px;"></div>
  </div>
</template>

<script>

import Highcharts from 'highcharts'
import HighchartsExporting from 'highcharts/modules/exporting'
import ExportData from 'highcharts/modules/export-data'

HighchartsExporting(Highcharts)
ExportData(Highcharts)

Highcharts.setOptions({
  accessibility: {enabled: false}
})

export default {
  name: 'HallmarkDonut',
  props: {
    proteinId: {
      type: String,
      required: true
    },
    hallmarkValues: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      chartInstance: null,
      hallmarkMeta: [
        {name: "evading growth suppressors", color: "#A1887F"},
        {name: "avoiding immune destruction", color: "#FF00FF"},
        {name: "enabling replicative immortality", color: "#0288D1"},
        {name: "tumor promoting inflammation", color: "#FFA726"},
        {name: "activating invasion and metastasis", color: "#757575"},
        {name: "inducing angiogenesis", color: "#EF5350"},
        {name: "genomic instability and mutation", color: "#5C6BC0"},
        {name: "resisting cell death", color: "#BDBDBD"},
        {name: "deregulating cellular energetics", color: "#9575CD"},
        {name: "sustaining proliferative signaling", color: "#00E676"}
      ]
    }
  },
  mounted() {
    this.renderChart()
  },
  watch: {
    hallmarkValues: {
      deep: true,
      handler() {
        if (!this.chartInstance) return
        this.updateSeries()
      }
    }
  },
  beforeDestroy() {
    if (this.chartInstance) this.chartInstance.destroy()
  },
  methods: {
    renderChart() {
      const {pieData, highlightRing} = this.getSeriesData()

      this.chartInstance = Highcharts.chart(this.$refs.chartContainer, {
        chart: {
          type: 'pie',
          backgroundColor: 'transparent',
          animation: false
        },
        title: {
          text: this.proteinId || '',
          verticalAlign: 'middle',
          floating: true,
          style: {
            fontSize: '18px',
            fontWeight: 'bold'
          }
        },
        tooltip: {
          formatter: function () {
            return `
      <b>${this.point.name}</b><br/>
      #Publications: ${this.point.publication ?? 0}<br/>
      #Sentences: ${this.point.sentence ?? 0}
    `
          }
        },

        plotOptions: {
          pie: {
            animation: false,
            innerSize: '90%',
            borderColor: 'white',
            borderWidth: 1,
            dataLabels: {enabled: false}
          }
        },
        series: [
          {
            name: 'Hallmarks',
            size: '80%',
            innerSize: '50%',
            data: pieData,
            point: {
              events: {
                click: (e) => {
                  const name = e.point.name
                  const val = this.hallmarkValues?.[name] ?? {sentence: 0, publication: 0}
                  if (val.publication > 0) this.$emit('hallmark-click', name)
                },
                mouseOver: function () {
                  const ring = this.series.chart.get('highlight')
                  const label = ring?.points.find(p => p.name === this.name)
                  if (label?.dataLabel) label.dataLabel.hide()
                },
                mouseOut: function () {

                  const ring = this.series.chart.get('highlight')
                  const label = ring?.points.find(p => p.name === this.name)
                  if (label?.dataLabel) label.dataLabel.show()
                }
              }
            }
          },
          {
            id: 'highlight',
            name: 'Highlight',
            size: '90%',
            innerSize: '89%',
            enableMouseTracking: false,
            data: highlightRing,
            states: {
              hover: {enabled: false}
            }
          }
        ],
        exporting: {
          enabled: true,
          scale: 8,
          chartOptions: {
            chart: {
              width: 400,
              height: 400
            }
          },
          buttons: {
            contextButton: {
              menuItems: ["downloadPNG", "downloadSVG"]
            }
          }
        },
        credits: {enabled: false}
      })
    },
    getSeriesData() {

      const pieData = this.hallmarkMeta.map(h => {
        const sentence = this.hallmarkValues?.[h.name]?.sentence ?? 0
        const publication = this.hallmarkValues?.[h.name]?.publication ?? 0

        return {
          name: h.name,
          y: 1,
          color: h.color,
          publication,
          sentence
        }
      })

      const highlightRing = this.hallmarkMeta.map(h => {
        const sentence = this.hallmarkValues?.[h.name]?.sentence ?? 0
        const publication = this.hallmarkValues?.[h.name]?.publication ?? 0

        let color = 'rgba(0,0,0,0)'
        let label = ''

        if (publication > 1) {
          color = h.color
          label = `${h.name.split(' ').join('<br/>')} <br/>(${publication}/${sentence})`
        } else if (publication === 1) {
          color = Highcharts.color(h.color).setOpacity(0.2).get()
        }

        return {
          name: h.name,
          y: 2,
          color,
          publication,
          sentence,
          dataLabels: {
            enabled: publication > 1,
            useHTML: false,
            format: label,
            y: -16,
            x: 2,
            distance: -52,
            style: {
              color: 'white',
              fontWeight: 'bold',
              fontSize: '10px',
              textOutline: 'none',
              textAlign: 'center',
              lineHeight: '12px'
            }
          }
        }
      })

      return {pieData, highlightRing}
    },
    updateSeries() {
      const {pieData, highlightRing} = this.getSeriesData()

      const base = this.chartInstance.series.find(s => s.name === 'Hallmarks')
      const ring = this.chartInstance.get('highlight')

      if (!base || !ring) return
      base.setData(pieData, false)
      ring.setData(highlightRing, false)

      this.chartInstance.customValues = this.hallmarkValues
      this.chartInstance.redraw()
    },
  }
}
</script>

